import React, { useEffect, useState, useCallback } from "react";
import { Search, Loader2, Crown, Edit3, Building2 } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import AdminCompanyEditor from "./AdminCompanyEditor";

const PLAN_COLOR = {
  basic: "bg-blue-500/15 text-blue-200 border-blue-500/30",
  pro: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30",
  enterprise: "bg-purple-500/15 text-purple-200 border-purple-500/30",
};

export default function AdminCompaniesTable() {
  const { lang } = useI18n();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/companies",
                              { params: { q: q || undefined, limit: 200 } });
      setRows(r.data.companies || []);
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const tr = (ca, es) => (lang === "es" ? es : ca);

  return (
    <div className="card-surface p-5" data-testid="admin-companies-table">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Building2 size={14} className="text-slate-400" />
          <h2 className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
            {tr("Gestió d'empreses", "Gestión de empresas")}
          </h2>
          <span className="text-xs text-slate-500 font-mono">({rows.length})</span>
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-md ml-auto">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            placeholder={tr("Cerca per nom...", "Buscar por nombre...")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-9 bg-white/5 border-white/10 text-white text-sm"
            data-testid="admin-companies-search" />
        </div>
      </div>

      {loading ? (
        <div className="py-10 text-center text-slate-400">
          <Loader2 className="w-5 h-5 mx-auto animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-6">
          {tr("Cap empresa coincideix", "Ninguna empresa coincide")}
        </p>
      ) : (
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-white/5">
                <th className="text-left px-3 py-2">{tr("Empresa", "Empresa")}</th>
                <th className="text-left px-3 py-2 hidden md:table-cell">NIF</th>
                <th className="text-left px-3 py-2">{tr("Pla", "Plan")}</th>
                <th className="text-left px-3 py-2 hidden sm:table-cell">{tr("Restant", "Restante")}</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c, i) => {
                const isOwner = c.is_owner;
                const planLabel = isOwner ? "enterprise" : (c.plan || "—");
                const daysLeft = c.days_remaining;
                const expired = daysLeft != null && daysLeft <= 0;
                return (
                  <tr key={c.id}
                      className="border-b border-white/5 hover:bg-white/[0.02]"
                      data-testid={`admin-company-row-${i}`}>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2 min-w-0">
                        {isOwner && <Crown size={12} className="text-amber-400 flex-shrink-0" />}
                        <span className="text-slate-200 truncate">{c.name}</span>
                        {c.is_trial && (
                          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-purple-500/20 text-purple-200 border border-purple-500/40 flex-shrink-0">
                            Trial
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs text-slate-400 hidden md:table-cell">
                      {c.nif || "—"}
                    </td>
                    <td className="px-3 py-2.5">
                      {c.plan || isOwner ? (
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border capitalize ${
                          PLAN_COLOR[planLabel] || "bg-white/5 text-slate-300 border-white/10"
                        }`}>
                          {planLabel}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-500">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 hidden sm:table-cell">
                      {isOwner ? (
                        <span className="text-xs text-amber-300">∞</span>
                      ) : daysLeft != null ? (
                        <span className={`text-xs font-mono ${
                          expired ? "text-rose-300"
                          : daysLeft <= 7 ? "text-amber-300"
                          : "text-slate-400"
                        }`}>
                          {expired ? tr("expirat", "expirado") : `${Math.floor(daysLeft)}d`}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-500">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditing(c)}
                        className="h-7 px-2.5 bg-white/5 border-white/10 text-slate-300 hover:bg-blue-500/20 hover:border-blue-500/40 hover:text-blue-100"
                        data-testid={`admin-edit-company-${i}`}>
                        <Edit3 size={12} className="mr-1.5" />
                        {tr("Edita", "Editar")}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AdminCompanyEditor
        open={!!editing}
        company={editing}
        onClose={() => setEditing(null)}
        onChanged={load} />
    </div>
  );
}
